import { AlertTriangle, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useMe } from '@/api/domains/merchant-settings';
import { useRecheckPlan } from '@/api/domains/plan';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const SHOPIFY_SUPPORT_URL = 'https://help.shopify.com/en/support';

/**
 * Full-screen plan-lock screen shown when the merchant's Shopify plan can't use
 * the carrier-service (calculated shipping) API, so InPost rates can't appear at
 * checkout. Mirrors the old AppLock/AuthorizationPlanUpdate: a warning banner,
 * plan-by-plan upgrade guidance, and a "Re-check plan" button that re-evaluates
 * eligibility (and lets the merchant back into the app once it clears).
 */
export function PlanRequiredPage() {
  const { t } = useTranslation('plan');
  const meQuery = useMe();
  const recheck = useRecheckPlan();

  const merchant = meQuery.data?.merchant;

  // Eligible (or not-yet-determined) merchants shouldn't sit on the lock screen.
  // Mirrors CarrierServiceGate, which only blocks on a definitive `false`.
  if (merchant && merchant.carrier_service_eligible !== false) {
    return <Navigate to="/" replace />;
  }

  const planSettingsUrl = merchant
    ? `https://admin.shopify.com/store/${merchant.shop_domain.replace('.myshopify.com', '')}/settings/plan`
    : 'https://admin.shopify.com';

  const stillBlocked = recheck.isSuccess && merchant?.carrier_service_eligible === false;

  return (
    <div className="min-h-full w-full overflow-y-auto bg-[#f7f7f7] py-12">
      <div className="mx-auto flex w-full max-w-[760px] flex-col gap-8 px-6 bg-white py-6 rounded-md">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-medium tracking-[-0.9px] text-black mb-2">{t('title')}</h1>
          <div className="flex gap-3 rounded-xl border border-[#ffcc05] bg-[#fff8e1] p-4">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-[#8a6d00]" aria-hidden />
            <div className="flex flex-col gap-1">
              <p className="font-medium text-black">{t('banner.title')}</p>
              <p className="text-sm text-muted-foreground">{t('banner.content')}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-medium tracking-[-0.6px] text-black">{t('whatToDo')}</h2>
          <p className="text-base text-muted-foreground">{t('requiredInfo')}</p>
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('basic.title')}</CardTitle>
              <CardDescription>{t('basic.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ExternalLinkButton href={planSettingsUrl}>{t('managePlan')}</ExternalLinkButton>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('shopify.title')}</CardTitle>
              <CardDescription>{t('shopify.description')}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
                <li>{t('shopify.option1')}</li>
                <li>{t('shopify.option2')}</li>
              </ul>
              <div className="flex flex-wrap gap-3">
                <ExternalLinkButton href={planSettingsUrl}>{t('managePlan')}</ExternalLinkButton>
                <ExternalLinkButton href={SHOPIFY_SUPPORT_URL} variant="outline">
                  {t('contactSupport')}
                </ExternalLinkButton>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('advanced.title')}</CardTitle>
              <CardDescription>{t('advanced.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ExternalLinkButton href={planSettingsUrl}>{t('managePlan')}</ExternalLinkButton>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#ededed] pt-6">
          <p className="text-sm text-muted-foreground">{t('recheck.hint')}</p>
          <Button
            type="button"
            onClick={() => recheck.mutate()}
            disabled={recheck.isPending}
            className="w-fit gap-2"
          >
            {recheck.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {t('recheck.checking')}
              </>
            ) : (
              <>
                <RefreshCw className="size-4" aria-hidden />
                {t('recheck.button')}
              </>
            )}
          </Button>
          {stillBlocked ? (
            <p className="text-sm text-[#b42318]">{t('recheck.stillBlocked')}</p>
          ) : null}
          {recheck.isError ? <p className="text-sm text-[#b42318]">{t('recheck.error')}</p> : null}
        </div>
      </div>
    </div>
  );
}

function ExternalLinkButton({
  href,
  variant = 'default',
  children,
}: {
  href: string;
  variant?: 'default' | 'outline';
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={cn(buttonVariants({ variant, size: 'sm' }), 'gap-2')}
    >
      {children}
      <ExternalLink className="size-4" aria-hidden />
    </a>
  );
}
